import {useRef, useState} from "react";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import type {IAppError} from "@pg-mono/request-state";

import {IRPStore} from "../../../app/rp_reducer";
import {addNotification} from "../../../notifications/state/notifications_slice";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {userSignUpHit} from "../../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {UserAccountType} from "../../../user/types/UserAccountType";
import {authSucceeded} from "../../actions/auth_succeeded_event";
import {emailSignUp} from "../../api/email_sign_up";
import {registerFormExtendedWithoutMarketingValidationSchema} from "../../constants/register_form_extended_without_marketing_validation_schema";
import {registerFormExtendedWithoutMarketingValues} from "../../constants/register_form_extended_without_marketing_values";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../../tracking/auth_analytics";
import {AuthActionType} from "../../types/AuthActionType";
import {IRegisterExtendedWithoutMarketing} from "../../types/IRegisterExtendedWithoutMarketing";
import {RegisterFormPhoneVerificationInfo} from "./RegisterFormPhoneVerificationInfo";

interface IProps {
    onAuthSuccess: (authActionType: AuthActionType) => void;
    userSource: UserSource;
    gtmSourceSection?: string;
    registerButtonText?: string;
    gtmEventData?: () => void;
}

export const ConsistentRegisterForm = (props: IProps) => {
    const {onAuthSuccess, userSource, gtmSourceSection, registerButtonText = "ZAŁÓŻ KONTO", gtmEventData} = props;

    const dispatch = useAppDispatch();
    const currentViewType = useAppSelector((state) => state.viewType.current);
    const countryCodes = useSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const formRef = useRef<HTMLFormElement>(null);

    const methods = useFormRHF<IRegisterExtendedWithoutMarketing>({
        defaultValues: registerFormExtendedWithoutMarketingValues,
        resolver: yupResolver(registerFormExtendedWithoutMarketingValidationSchema)
    });

    const handleFieldFocus = () => {
        formRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });
    };

    const onSubmit = (data: IRegisterExtendedWithoutMarketing) => {
        setIsSubmitting(true);
        emailSignUp({
            email: data.email,
            name: data.name,
            phone: `${data.phone.code} ${data.phone.number}`,
            type: UserAccountType.LOCAL,
            source: userSource
        })
            .then(async (response) => {
                authAnalytics.gtm.emailAuthEvent({
                    event: GAAuthEventName.SIGN_UP,
                    eventCategory: GAAuthEventCategory.REGISTRATION_FORM,
                    source: gtmSourceSection ?? null,
                    userSource
                });

                dispatch(
                    authSucceeded({
                        type: AuthActionType.REGISTRATION,
                        userSource,
                        gtmSourceSection,
                        registrationMethod: "email"
                    })
                );

                userSignUpHit(userSource, response, currentViewType);
                gtmEventData?.();
                onAuthSuccess(AuthActionType.REGISTRATION);
            })
            .catch((error: {appError?: IAppError}) => {
                if (error.appError?.fieldErrors) {
                    for (const field of Object.keys(error.appError.fieldErrors)) {
                        const message = error.appError.fieldErrors[field];

                        methods.setError(field as keyof IRegisterExtendedWithoutMarketing, {
                            message: Array.isArray(message) ? message.join(", ") : message
                        });
                    }
                }

                if (error.appError?.nonFieldErrors && error.appError.nonFieldErrors.length) {
                    dispatch(addNotification({notification: {content: error.appError.nonFieldErrors.join(" "), type: "failure"}}));
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <FormProviderRHF {...methods}>
            <div className={formWrapperStyle}>
                <form ref={formRef} className={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
                    <InputField name="name" placeholder="Imię i nazwisko" onFocus={handleFieldFocus} />
                    <InputField name="email" placeholder="E-mail" onFocus={handleFieldFocus} />
                    <PhoneFieldControlled name="phone" codes={countryCodes} placeholder="Telefon" afterInput={<RegisterFormPhoneVerificationInfo />} />
                    <div className={buttonWrapperStyle}>
                        <Button className={submitButtonStyle} type="submit" variant="filled_primary" isLoading={isSubmitting}>
                            {registerButtonText}
                        </Button>
                    </div>

                    <Text variant="info_txt_2" color="var(--colors-gray-800)" className={regulationsStyle}>
                        Rejestracja konta oznacza zgodę na zawarcie z Property Group sp. z o.o. umowy na świadczenie usług automatycznego dopasowania i
                        zarządzania ofertami nieruchomości w ramach konta mieszkaniowego na warunkach określonych w Regulaminie. Rejestrując się akceptuję{" "}
                        <a href="https://rynekpierwotny.pl/regulamin/" target="_blank" className={linkStyle}>
                            regulamin serwisu
                        </a>{" "}
                        i zgadzam się na świadczenie usługi automatycznego dopasowania ofert.
                    </Text>
                </form>
            </div>
        </FormProviderRHF>
    );
};

const formWrapperStyle = css({
    display: "flex",
    flexDirection: "column"
});

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5
});

const buttonWrapperStyle = css({
    position: "sticky",
    backgroundColor: "var(--colors-white)",
    pt: 2,
    zIndex: 10,
    bottom: 0,
    md: {
        bottom: "calc(-4 * var(--globals-baseUnit))"
    }
});

const submitButtonStyle = css({
    width: "100%"
});

const linkStyle = css({
    textDecoration: "underline",
    color: "var(--colors-gray-800)"
});

const regulationsStyle = css({
    pb: 1,
    borderBottom: "1px solid var(--colors-gray-200)"
});
import {useRef, useState} from "react";
import {useSelector} from "react-redux";
import {yupResolver} from "@hookform/resolvers/yup";
import {css} from "@panda/css";

import {Button} from "@pg-design/button-module";
import {InputField, PhoneFieldControlled} from "@pg-design/rhf-components-module";
import {Text} from "@pg-design/text-module";
import {FormProviderRHF, useFormRHF} from "@pg-mono/react-hook-forms-utils";
import type {IAppError} from "@pg-mono/request-state";

import {IRPStore} from "../../../app/rp_reducer";
import {addNotification} from "../../../notifications/state/notifications_slice";
import {useAppDispatch, useAppSelector} from "../../../store/hooks";
import {userSignUpHit} from "../../../tracking/algolytics_hits/user_sign_up_hit";
import {UserSource} from "../../../tracking/tracking_utils/user_source";
import {UserAccountType} from "../../../user/types/UserAccountType";
import {authSucceeded} from "../../actions/auth_succeeded_event";
import {emailSignUp} from "../../api/email_sign_up";
import {registerFormExtendedWithoutMarketingValidationSchema} from "../../constants/register_form_extended_without_marketing_validation_schema";
import {registerFormExtendedWithoutMarketingValues} from "../../constants/register_form_extended_without_marketing_values";
import {authAnalytics, GAAuthEventCategory, GAAuthEventName} from "../../tracking/auth_analytics";
import {AuthActionType} from "../../types/AuthActionType";
import {IRegisterExtendedWithoutMarketing} from "../../types/IRegisterExtendedWithoutMarketing";
import {RegisterFormPhoneVerificationInfo} from "./RegisterFormPhoneVerificationInfo";

interface IProps {
    onAuthSuccess: (authActionType: AuthActionType) => void;
    userSource: UserSource;
    gtmSourceSection?: string;
    registerButtonText?: string;
    gtmEventData?: () => void;
}

export const ConsistentRegisterForm = (props: IProps) => {
    const {onAuthSuccess, userSource, gtmSourceSection, registerButtonText = "ZAŁÓŻ KONTO", gtmEventData} = props;

    const dispatch = useAppDispatch();
    const currentViewType = useAppSelector((state) => state.viewType.current);
    const countryCodes = useSelector((state: IRPStore) => state.variables.data?.country_codes ?? []);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const formRef = useRef<HTMLFormElement>(null);

    const methods = useFormRHF<IRegisterExtendedWithoutMarketing>({
        defaultValues: registerFormExtendedWithoutMarketingValues,
        resolver: yupResolver(registerFormExtendedWithoutMarketingValidationSchema)
    });

    const handleFieldFocus = () => {
        formRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "start"
        });
    };

    const onSubmit = (data: IRegisterExtendedWithoutMarketing) => {
        setIsSubmitting(true);
        emailSignUp({
            email: data.email,
            name: data.name,
            phone: `${data.phone.code} ${data.phone.number}`,
            type: UserAccountType.LOCAL,
            source: userSource
        })
            .then(async (response) => {
                authAnalytics.gtm.emailAuthEvent({
                    event: GAAuthEventName.SIGN_UP,
                    eventCategory: GAAuthEventCategory.REGISTRATION_FORM,
                    source: gtmSourceSection ?? null,
                    userSource
                });

                dispatch(
                    authSucceeded({
                        type: AuthActionType.REGISTRATION,
                        userSource,
                        gtmSourceSection,
                        registrationMethod: "email"
                    })
                );

                userSignUpHit(userSource, response, currentViewType);
                gtmEventData?.();
                onAuthSuccess(AuthActionType.REGISTRATION);
            })
            .catch((error: {appError?: IAppError}) => {
                if (error.appError?.fieldErrors) {
                    for (const field of Object.keys(error.appError.fieldErrors)) {
                        const message = error.appError.fieldErrors[field];

                        methods.setError(field as keyof IRegisterExtendedWithoutMarketing, {
                            message: Array.isArray(message) ? message.join(", ") : message
                        });
                    }
                }

                if (error.appError?.nonFieldErrors && error.appError.nonFieldErrors.length) {
                    dispatch(addNotification({notification: {content: error.appError.nonFieldErrors.join(" "), type: "failure"}}));
                }
            })
            .finally(() => {
                setIsSubmitting(false);
            });
    };

    return (
        <FormProviderRHF {...methods}>
            <div className={formWrapperStyle}>
                <form ref={formRef} className={formStyle} onSubmit={methods.handleSubmit(onSubmit)}>
                    <InputField name="name" placeholder="Imię i nazwisko" onFocus={handleFieldFocus} />
                    <InputField name="email" placeholder="E-mail" onFocus={handleFieldFocus} />
                    <PhoneFieldControlled name="phone" codes={countryCodes} placeholder="Telefon" afterInput={<RegisterFormPhoneVerificationInfo />} />
                    <div className={buttonWrapperStyle}>
                        <Button className={submitButtonStyle} type="submit" variant="filled_primary" isLoading={isSubmitting}>
                            {registerButtonText}
                        </Button>
                    </div>

                    <Text variant="info_txt_2" color="var(--colors-gray-800)" className={regulationsStyle}>
                        Rejestracja konta oznacza zgodę na zawarcie z Property Group sp. z o.o. umowy na świadczenie usług automatycznego dopasowania i
                        zarządzania ofertami nieruchomości w ramach konta mieszkaniowego na warunkach określonych w Regulaminie. Rejestrując się akceptuję{" "}
                        <a href="https://rynekpierwotny.pl/regulamin/" target="_blank" className={linkStyle}>
                            regulamin serwisu
                        </a>{" "}
                        i zgadzam się na świadczenie usługi automatycznego dopasowania ofert.
                    </Text>
                </form>
            </div>
        </FormProviderRHF>
    );
};

const formWrapperStyle = css({
    display: "flex",
    flexDirection: "column"
});

const formStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5
});

const buttonWrapperStyle = css({
    position: "sticky",
    backgroundColor: "var(--colors-white)",
    pt: 2,
    zIndex: 10,
    bottom: 0,
    md: {
        bottom: "calc(-4 * var(--globals-baseUnit))"
    }
});

const submitButtonStyle = css({
    width: "100%"
});

const linkStyle = css({
    textDecoration: "underline",
    color: "var(--colors-gray-800)"
});

const regulationsStyle = css({
    pb: 1,
    borderBottom: "1px solid var(--colors-gray-200)"
});
